import { AccountFundingSummary } from '@/components/Account/AccountFundingSummary';
import { SubtitleBox } from '@/components/Account/AccountFundingSummary.utils';
import { InvoiceDocumentList } from '@/components/Payouts/InvoiceDocumentList';
import { Quotes } from '@/components/Quotes/Quotes';
import { useBackfillPricingSettingLockDateMutation } from '@/services/accounts';
import { useGetDocumentsQuery } from '@/services/documents';
import { useGetProjectByExternalIdQuery } from '@/services/payouts';
import { Alert, Box, Card } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import {
  BillingDocumentTypes,
  DirectPayMaterialVendorDisplayNameOptions,
  InstallDocumentTypes,
  MaterialsInvoiceStatus,
  ProgramType,
} from 'types';

import { MainContainer } from '..';
import { AccountProjectTransactionTable } from '../Payouts/AccountProjectTransactionsTable';
import { PricingSettingsDetails } from '../Payouts/PricingSettingsDetails';

export const AccountFundingDetails = ({ account }: { account: any }) => {
  const { id } = useParams<{ id: string }>();
  const [backfillPricingSettingLockDate] = useBackfillPricingSettingLockDateMutation();
  const { enableHoldbackSelfService = false } = useFlags();
  const { data: project, isLoading: isProjectLoading } = useGetProjectByExternalIdQuery(id || skipToken);
  const { data: invoices = [], isLoading: isInvoicesLoading } = useGetDocumentsQuery(
    {
      accountId: account?.id || '',
      documentTypeFilter: [InstallDocumentTypes.invoice, BillingDocumentTypes.incomingDirectPayInvoice],
    },
    { skip: !account?.id },
  );

  useEffect(() => {
    if (account?.id && !account?.pricingSettingLockDate) {
      backfillPricingSettingLockDate(account.id);
    }
  }, [account?.id, account?.pricingSettingLockDate, backfillPricingSettingLockDate]);

  const isSolarOrNewHomes = [ProgramType.solar, ProgramType.newHomes].includes(account?.programType);
  const isLoading = isProjectLoading || isInvoicesLoading;
  const vendorAlias = account?.directPay
    ?.directPayMaterialVendorAlias as keyof typeof DirectPayMaterialVendorDisplayNameOptions;

  const showMissingInvoiceWarning = useMemo(() => {
    if (!project) return false;
    if (!project.materials) return false;
    return project.materials?.invoiceStatus === MaterialsInvoiceStatus.missing;
  }, [project]);

  const materialVendorDisplayName = useMemo(() => {
    if (vendorAlias) {
      return DirectPayMaterialVendorDisplayNameOptions[vendorAlias];
    }
    return 'vendor';
  }, [vendorAlias]);

  return (
    <>
      <Helmet>
        <title>Account Funding</title>
      </Helmet>
      <MainContainer maxWidth="100" gap="sm">
        {!isLoading && account && (
          <>
            {showMissingInvoiceWarning && (
              <Alert
                variant="warning"
                hasIcon
                title={`Payment for this account cannot be issued until ${materialVendorDisplayName} material invoices are received. Please reach out to ${materialVendorDisplayName} to finalize the invoice.`}
              />
            )}
            <AccountFundingSummary account={account} />
            <Card padding="sm">
              <Card.Header>
                <Box direction="row" justifyContent="space-between">
                  <SubtitleBox>TRANSACTIONS</SubtitleBox>
                </Box>
              </Card.Header>
              <Box>
                <AccountProjectTransactionTable />
              </Box>
            </Card>
            <Card padding="sm">
              <Card.Header>
                <Box direction="row" justifyContent="space-between">
                  <SubtitleBox>INVOICES</SubtitleBox>
                </Box>
              </Card.Header>
              {!isInvoicesLoading && <InvoiceDocumentList invoices={invoices} isLoading={isInvoicesLoading} />}
            </Card>
            <Card padding="sm">
              <Card.Header>
                <Box direction="row" justifyContent="space-between">
                  <SubtitleBox>QUOTES</SubtitleBox>
                </Box>
              </Card.Header>
              <Quotes hideHeader />
            </Card>
            {enableHoldbackSelfService && isSolarOrNewHomes && <PricingSettingsDetails account={account} />}
          </>
        )}
      </MainContainer>
    </>
  );
};
